import ko from 'knockout';
import _ from 'underscore';
import { DeterministicView } from '@bingads-webui/deterministic-view';

/**
 * Base class for DeterministicView using KO bindings. It does 2 things
 * 1. Create a view model object on initialization
 * 2. Reset the KO binding every time the view is rendered
 */
export class KnockoutView extends DeterministicView {
  initialize(options) {
    this.viewModel = new this.ViewModel(options);
  }

  /**
   * The type of the view model
   * @type {Function}
   */
  get ViewModel() {
    return Object;
  }

  /**
   * In addition to `DeterministicView#renderTrunk`, it clears the existing
   * binding and reapply the bindings to the root element
   * @override
   * @returns {KnockoutView} - The view itself
   */
  renderTrunk() {
    ko.cleanNode(this.el);
    super.renderTrunk();
    this.delegateEvents();
    ko.applyBindings(this.viewModel, this.el);
    return this;
  }
}

/**
 * A specialized KnockoutView for the leaf views whose state is isomorphic to
 * their view model
 */
export class PureKnockoutView extends KnockoutView {
  /**
   * Write the state to viewModel
   * @param {Object} state - The new state
   * @returns {PureKnockoutView} - The view itself
   */
  setState(state) {
    _.each(this.viewModel, (observable, key) => {
      // Only read-write observables are included in the state
      if (_.has(state, key) && ko.isWritableObservable(observable)) {
        observable.call(this.viewModel, state[key]);
      }
    });
    return this;
  }

  /**
   * Read the state from viewModel
   * @returns {Object} - The state of the view
   */
  getState() {
    const state = {};

    _.each(this.viewModel, (observable, key) => {
      // Only read-write observables are included in the state
      if (ko.isWritableObservable(observable)) {
        state[key] = observable.call(this.viewModel);
      }
    });

    return state;
  }
}
